// HBM Theme JS
(function () {
  'use strict';

  // ============ UTILITIES ============
  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  // The Predictive Search API (/search/suggest.json) returns money fields as
  // DECIMAL STRINGS in the store currency (e.g. "29.95"), NOT integer cents like
  // the cart/product .js APIs. Format those directly — do NOT divide by 100.
  function formatMoneyFromDecimal(value) {
    var amount = parseFloat(value);
    if (isNaN(amount)) return '';
    return '$' + amount.toFixed(2);
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function showToast(message, action) {
    const container = document.querySelector('[data-toast-container]');
    if (!container) return;

    const toast = document.createElement('div');
    toast.className = 'toast';
    toast.setAttribute('role', 'status');

    let html = '<div class="toast-icon">✓</div>';
    html += '<div class="toast-text">' + escapeHtml(message) + '</div>';
    if (action && action.url && action.text) {
      html += '<a href="' + escapeHtml(action.url) + '" class="toast-action">' + escapeHtml(action.text) + ' →</a>';
    }
    toast.innerHTML = html;
    container.appendChild(toast);

    requestAnimationFrame(function () { toast.classList.add('show'); });

    setTimeout(function () {
      toast.classList.remove('show');
      setTimeout(function () { toast.remove(); }, 300);
    }, 4000);
  }

  // ============ MOBILE DRAWER ============
  function initMobileDrawer() {
    const toggle = document.querySelector('[data-menu-toggle]');
    const drawer = document.querySelector('[data-mobile-drawer]');
    const backdrop = document.querySelector('[data-drawer-backdrop]');
    const close = document.querySelector('[data-drawer-close]');

    if (!toggle || !drawer || !backdrop) return;

    function openDrawer() {
      drawer.classList.add('is-open');
      backdrop.classList.add('is-open');
      backdrop.hidden = false;
      document.body.classList.add('drawer-open');
      toggle.setAttribute('aria-expanded', 'true');
      drawer.setAttribute('aria-hidden', 'false');
      const firstFocusable = drawer.querySelector('a, button, input');
      if (firstFocusable) firstFocusable.focus();
    }

    function closeDrawer() {
      drawer.classList.remove('is-open');
      backdrop.classList.remove('is-open');
      document.body.classList.remove('drawer-open');
      toggle.setAttribute('aria-expanded', 'false');
      drawer.setAttribute('aria-hidden', 'true');
      setTimeout(function () {
        if (!backdrop.classList.contains('is-open') && !document.querySelector('[data-cart-drawer].is-open')) {
          backdrop.hidden = true;
        }
      }, 350);
      toggle.focus();
    }

    toggle.addEventListener('click', openDrawer);
    if (close) close.addEventListener('click', closeDrawer);
    backdrop.addEventListener('click', closeDrawer);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) closeDrawer();
    });

    drawer.querySelectorAll('a').forEach(function (link) {
      link.addEventListener('click', function () {
        setTimeout(closeDrawer, 100);
      });
    });
  }

  // ============ PREDICTIVE SEARCH ============
  function initPredictiveSearch() {
    const form = document.querySelector('[data-predictive-search-form]');
    if (!form) return;

    const input = form.querySelector('[data-predictive-search-input]');
    const results = form.querySelector('[data-predictive-search-results]');
    const content = form.querySelector('[data-predictive-search-content]');
    const loading = form.querySelector('[data-predictive-search-loading]');

    if (!input || !results || !content) return;

    let debounceTimer;
    let abortController;

    function renderResults(data, query) {
      const products = (data.resources && data.resources.results && data.resources.results.products) || [];

      if (products.length === 0) {
        content.innerHTML =
          '<div class="predictive-search-empty">No products found for "' + escapeHtml(query) + '"</div>';
        return;
      }

      let html = '<div class="predictive-search-section">';
      html += '<div class="predictive-search-section-title">Products</div>';

      products.slice(0, 6).forEach(function (product) {
        const img = (product.featured_image && product.featured_image.url) || product.image || '';
        const vendor = product.vendor || '';
        // suggest.json prices are decimal strings (dollars) — compare numerically.
        const priceMin = parseFloat(product.price_min);
        const priceMax = parseFloat(product.price_max);
        const priceVaries = !isNaN(priceMax) && priceMax > priceMin;
        const onSale = parseFloat(product.compare_at_price_min) > priceMin;

        html += '<a href="' + escapeHtml(product.url) + '" class="predictive-result" role="option">';
        if (img) {
          html += '<img src="' + escapeHtml(img) + '" alt="" class="predictive-result-img" loading="lazy">';
        } else {
          html += '<div class="predictive-result-img"></div>';
        }
        html += '<div class="predictive-result-info">';
        html += '<div class="predictive-result-title">' + escapeHtml(product.title) + '</div>';
        if (vendor) html += '<div class="predictive-result-meta">' + escapeHtml(vendor) + '</div>';
        html += '</div>';
        html += '<div class="predictive-result-price">';
        html += (priceVaries ? 'From ' : '') + formatMoneyFromDecimal(product.price_min);
        if (onSale) html += '<span class="was">' + formatMoneyFromDecimal(product.compare_at_price_min) + '</span>';
        html += '</div>';
        html += '</a>';
      });

      html += '</div>';
      html +=
        '<a class="predictive-search-all" href="/search?q=' +
        encodeURIComponent(query) +
        '">View all results →</a>';

      content.innerHTML = html;
    }

    function showResults() {
      results.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    }
    function hideResults() {
      results.hidden = true;
      input.setAttribute('aria-expanded', 'false');
    }

    function fetchResults(query) {
      if (abortController) abortController.abort();
      abortController = new AbortController();

      if (loading) loading.hidden = false;
      content.innerHTML = '';
      showResults();

      const url =
        '/search/suggest.json?q=' + encodeURIComponent(query) +
        '&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last';

      fetch(url, { signal: abortController.signal })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (loading) loading.hidden = true;
          renderResults(data, query);
        })
        .catch(function (err) {
          if (err.name === 'AbortError') return;
          if (loading) loading.hidden = true;
          content.innerHTML = '<div class="predictive-search-empty">Search unavailable.</div>';
        });
    }

    input.addEventListener('input', function () {
      clearTimeout(debounceTimer);
      const query = input.value.trim();
      if (query.length < 2) { hideResults(); return; }
      debounceTimer = setTimeout(function () { fetchResults(query); }, 250);
    });

    input.addEventListener('focus', function () {
      if (input.value.trim().length >= 2 && content.children.length > 0) showResults();
    });

    document.addEventListener('click', function (e) {
      if (!form.contains(e.target)) hideResults();
    });

    input.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { hideResults(); input.blur(); }
    });
  }

  // ============ CART DRAWER ============
  function initCartDrawer() {
    const drawer = document.querySelector('[data-cart-drawer]');
    const backdrop = document.querySelector('[data-cart-drawer-backdrop]');
    const close = document.querySelector('[data-cart-drawer-close]');

    if (!drawer || !backdrop) return;

    function openCart() {
      drawer.classList.add('is-open');
      backdrop.classList.add('is-open');
      backdrop.hidden = false;
      document.body.classList.add('drawer-open');
      drawer.setAttribute('aria-hidden', 'false');
    }

    function closeCart() {
      drawer.classList.remove('is-open');
      backdrop.classList.remove('is-open');
      document.body.classList.remove('drawer-open');
      drawer.setAttribute('aria-hidden', 'true');
      setTimeout(function () {
        if (!backdrop.classList.contains('is-open')) backdrop.hidden = true;
      }, 350);
    }

    // Intercept cart icon click to open drawer instead of navigating
    document.querySelectorAll('.cart-btn').forEach(function (link) {
      link.addEventListener('click', function (e) {
        e.preventDefault();
        openCart();
      });
    });

    if (close) close.addEventListener('click', closeCart);
    backdrop.addEventListener('click', closeCart);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) closeCart();
    });

    // Cart drawer line-item quantity / remove handlers (delegated)
    drawer.addEventListener('click', function (e) {
      const qtyBtn = e.target.closest('[data-cart-qty-change]');
      const removeBtn = e.target.closest('[data-cart-remove]');

      if (qtyBtn) {
        e.preventDefault();
        const key = qtyBtn.dataset.cartQtyChange;
        const qty = parseInt(qtyBtn.dataset.qty, 10);
        if (qty < 0) return;
        updateCartItem(key, qty);
      } else if (removeBtn) {
        e.preventDefault();
        const key = removeBtn.dataset.cartRemove;
        updateCartItem(key, 0);
      }
    });

    // Expose globally
    window.HBM = window.HBM || {};
    window.HBM.openCart = openCart;
    window.HBM.closeCart = closeCart;
  }

  function updateCartItem(key, quantity) {
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (r) { return r.json(); })
      .then(function () {
        refreshCartDrawer();
        updateCartCount();
      });
  }

  function refreshCartDrawer() {
    return fetch('/?sections=cart-drawer')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        const html = data['cart-drawer'];
        if (!html) return;
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        const newBody = tmp.querySelector('[data-cart-drawer-body]');
        const oldBody = document.querySelector('[data-cart-drawer-body]');
        if (newBody && oldBody) oldBody.innerHTML = newBody.innerHTML;

        const newCount = tmp.querySelector('[data-cart-drawer-count]');
        const oldCount = document.querySelector('[data-cart-drawer-count]');
        if (newCount && oldCount) oldCount.textContent = newCount.textContent;
      });
  }

  function updateCartCount() {
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.querySelectorAll('[data-cart-count]').forEach(function (el) {
          el.textContent = cart.item_count;
        });
      });
  }

  // ============ AJAX ADD TO CART (product form) ============
  function initAjaxAddToCart() {
    document.querySelectorAll('form[action$="/cart/add"]').forEach(function (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        const btn = form.querySelector('[data-add-to-cart]') || form.querySelector('[type="submit"]');
        const textEl = form.querySelector('[data-add-to-cart-text]');
        const originalText = textEl ? textEl.textContent : (btn ? btn.textContent : 'Add to cart');

        if (btn) btn.disabled = true;
        if (textEl) textEl.textContent = 'Adding…';
        else if (btn) btn.textContent = 'Adding…';

        const formData = new FormData(form);

        fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        })
          .then(function (r) { return r.json().then(function (data) { return { ok: r.ok, data: data }; }); })
          .then(function (res) {
            if (!res.ok) {
              if (textEl) textEl.textContent = res.data.description || 'Unavailable';
              else if (btn) btn.textContent = res.data.description || 'Unavailable';
              setTimeout(function () {
                if (textEl) textEl.textContent = originalText;
                else if (btn) btn.textContent = originalText;
                if (btn) btn.disabled = false;
              }, 2000);
              return;
            }
            if (textEl) textEl.textContent = 'Added ✓';
            else if (btn) btn.textContent = 'Added ✓';
            updateCartCount();
            refreshCartDrawer();
            showToast(res.data.title ? (res.data.title + ' added to cart') : 'Added to cart', { url: '/cart', text: 'View cart' });
            setTimeout(function () {
              if (textEl) textEl.textContent = originalText;
              else if (btn) btn.textContent = originalText;
              if (btn) btn.disabled = false;
            }, 1500);
          })
          .catch(function () {
            if (textEl) textEl.textContent = originalText;
            else if (btn) btn.textContent = originalText;
            if (btn) btn.disabled = false;
          });
      });
    });
  }

  // ============ QUICK-ADD FROM PRODUCT CARDS ============
  function initQuickAdd() {
    document.addEventListener('click', function (e) {
      const btn = e.target.closest('[data-quick-add]');
      if (!btn) return;

      e.preventDefault();
      e.stopPropagation();

      const variantId = btn.dataset.quickAdd;
      const textEl = btn.querySelector('[data-quick-add-text]') || btn;
      const originalText = textEl.textContent;

      btn.disabled = true;
      textEl.textContent = 'Adding…';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      })
        .then(function (r) { return r.json().then(function (data) { return { ok: r.ok, data: data }; }); })
        .then(function (res) {
          if (!res.ok) {
            textEl.textContent = 'Unavailable';
          } else {
            textEl.textContent = 'Added ✓';
            updateCartCount();
            refreshCartDrawer();
            showToast(res.data.title ? (res.data.title + ' added to cart') : 'Added to cart', { url: '/cart', text: 'View cart' });
          }
          setTimeout(function () {
            textEl.textContent = originalText;
            btn.disabled = false;
          }, 1500);
        })
        .catch(function () {
          textEl.textContent = originalText;
          btn.disabled = false;
        });
    });
  }

  // ============ PRODUCT GALLERY THUMBNAILS ============
  function initProductGallery() {
    const main = document.querySelector('[data-product-main-image]');
    const thumbs = document.querySelectorAll('[data-product-thumb]');
    if (!main || thumbs.length === 0) return;

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        const src = thumb.dataset.imageSrc;
        if (src) main.src = src;
        thumbs.forEach(function (t) { t.classList.remove('active'); });
        thumb.classList.add('active');
      });
    });
  }

  // ============ VARIANT PICKER ============
  function initVariantPicker() {
    const section = document.querySelector('[data-product-section]');
    if (!section) return;

    const form = section.querySelector('form[action$="/cart/add"]');
    const variantJson = section.querySelector('[data-product-variants]');
    if (!form || !variantJson) return;

    let variants;
    try { variants = JSON.parse(variantJson.textContent); } catch (e) { return; }
    if (!variants || variants.length === 0) return;

    const options = form.querySelectorAll('[data-product-option]');
    const variantInput = form.querySelector('[data-product-variant-id]');
    const priceEl = section.querySelector('[data-price]');
    const priceWasEl = section.querySelector('[data-price-was]');
    const priceSaveEl = section.querySelector('[data-price-save]');
    const addBtn = form.querySelector('[data-add-to-cart]');
    const addText = form.querySelector('[data-add-to-cart-text]');
    const mainImg = section.querySelector('[data-product-main-image]');
    const thumbs = section.querySelectorAll('[data-product-thumb]');

    function update() {
      const selected = Array.from(options).map(function (o) { return o.value; });
      const variant = variants.find(function (v) {
        return v.options.every(function (val, i) { return val === selected[i]; });
      });

      if (!variant) {
        if (addBtn) addBtn.disabled = true;
        if (addText) addText.textContent = 'Unavailable';
        return;
      }

      if (variantInput) variantInput.value = variant.id;

      if (priceEl) priceEl.textContent = formatMoney(variant.price);
      if (priceWasEl) {
        if (variant.compare_at_price > variant.price) {
          priceWasEl.textContent = formatMoney(variant.compare_at_price);
          priceWasEl.hidden = false;
        } else {
          priceWasEl.hidden = true;
        }
      }
      if (priceSaveEl) {
        if (variant.compare_at_price > variant.price) {
          priceSaveEl.textContent = 'Save ' + formatMoney(variant.compare_at_price - variant.price);
          priceSaveEl.hidden = false;
        } else {
          priceSaveEl.hidden = true;
        }
      }

      if (variant.featured_image && mainImg) {
        mainImg.src = variant.featured_image.src;
        // Highlight matching thumbnail — prefer media.id, fall back to image.id
        const mediaId = (variant.featured_media && variant.featured_media.id) || variant.featured_image.id;
        thumbs.forEach(function (t) {
          t.classList.toggle('active', String(t.dataset.mediaId) === String(mediaId));
        });
      }

      // Update URL
      const url = new URL(window.location);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);

      // Button state
      if (variant.available) {
        if (addBtn) addBtn.disabled = false;
        if (addText) addText.textContent = (addBtn && addBtn.dataset.textAdd) || 'Add to cart';
      } else {
        if (addBtn) addBtn.disabled = true;
        if (addText) addText.textContent = (addBtn && addBtn.dataset.textSoldOut) || 'Sold out';
      }

      // Update stock indicator
      const stockEl = section.querySelector('[data-stock-indicator]');
      if (stockEl) {
        let html = '';
        if (variant.available) {
          if (variant.inventory_management && variant.inventory_policy !== 'continue' && variant.inventory_quantity > 0 && variant.inventory_quantity <= 5) {
            html = '<span class="stock-low">● Only ' + variant.inventory_quantity + ' left in stock</span>';
          } else {
            html = '<span class="stock-in">● In stock — ready to ship</span>';
          }
        } else {
          html = '<span class="stock-out">● Currently out of stock</span>';
        }
        stockEl.innerHTML = html;
      }
    }

    options.forEach(function (opt) { opt.addEventListener('change', update); });
  }

  // ============ COLLECTION FILTERS (auto-submit on change) ============
  function initCollectionFilters() {
    const filterForm = document.querySelector('[data-filter-form]');
    if (!filterForm) return;

    filterForm.addEventListener('change', function (e) {
      if (e.target.matches('input[type="checkbox"]')) {
        // Auto-submit when checkbox toggled
        filterForm.submit();
      }
    });

    // On mobile, collapse all filter groups by default (open on desktop)
    if (window.matchMedia && window.matchMedia('(max-width: 1100px)').matches) {
      filterForm.querySelectorAll('.filter-group[open]').forEach(function (d) {
        d.removeAttribute('open');
      });
    }
  }

  // ============ CART PAGE QTY AUTO-UPDATE ============
  function initCartQty() {
    document.querySelectorAll('.cart-item-qty input').forEach(function (input) {
      let timer;
      input.addEventListener('change', function () {
        clearTimeout(timer);
        timer = setTimeout(function () {
          const form = input.closest('form');
          if (form) form.submit();
        }, 300);
      });
    });
  }

  // ============ NEWSLETTER ============
  function initNewsletter() {
    document.querySelectorAll('form.newsletter-form-real').forEach(function (form) {
      form.addEventListener('submit', function () {
        const btn = form.querySelector('button[type="submit"]');
        if (btn) { btn.disabled = true; btn.textContent = 'Signing up…'; }
      });
    });
  }

  // ============ COOKIE BANNER ============
  function initCookieBanner() {
    const banner = document.querySelector('[data-cookie-banner]');
    if (!banner) return;

    const KEY = 'hbm_cookie_consent';
    let stored;
    try { stored = localStorage.getItem(KEY); } catch (e) { stored = null; }
    if (stored) return; // already responded

    setTimeout(function () { banner.hidden = false; }, 800);

    function respond(value) {
      try { localStorage.setItem(KEY, value); } catch (e) {}
      banner.hidden = true;
      // Hook for analytics: dispatch event so external tags can read it
      document.dispatchEvent(new CustomEvent('cookie:consent', { detail: { value: value } }));
    }

    const acceptBtn = banner.querySelector('[data-cookie-accept]');
    const declineBtn = banner.querySelector('[data-cookie-decline]');
    if (acceptBtn) acceptBtn.addEventListener('click', function () { respond('accepted'); });
    if (declineBtn) declineBtn.addEventListener('click', function () { respond('declined'); });
  }

  // ============ IMAGE ZOOM ============
  function initImageZoom() {
    const modal = document.querySelector('[data-zoom-modal]');
    if (!modal) return;

    const modalImg = modal.querySelector('[data-zoom-image]');
    const closeBtn = modal.querySelector('[data-zoom-close]');

    function open(src) {
      modalImg.src = src;
      modal.hidden = false;
      requestAnimationFrame(function () { modal.classList.add('is-open'); });
      document.body.classList.add('drawer-open');
    }
    function close() {
      modal.classList.remove('is-open');
      setTimeout(function () { modal.hidden = true; modalImg.src = ''; }, 300);
      document.body.classList.remove('drawer-open');
    }

    document.querySelectorAll('[data-zoomable]').forEach(function (img) {
      img.style.cursor = 'zoom-in';
      img.addEventListener('click', function () { open(img.src); });
    });

    closeBtn.addEventListener('click', close);
    modal.addEventListener('click', function (e) { if (e.target === modal) close(); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !modal.hidden) close();
    });
  }

  // ============ STICKY ADD-TO-CART BAR ============
  function initStickyAtc() {
    const bar = document.querySelector('[data-sticky-atc]');
    const mainForm = document.querySelector('form[action$="/cart/add"]');
    const mainBtn = mainForm ? mainForm.querySelector('[data-add-to-cart]') : null;
    const stickyBtn = bar ? bar.querySelector('[data-sticky-atc-btn]') : null;
    if (!bar || !mainForm || !mainBtn) return;

    const stickyPrice = bar.querySelector('[data-sticky-price]');
    const mainPrice = document.querySelector('[data-price]');

    function checkScroll() {
      const rect = mainBtn.getBoundingClientRect();
      const past = rect.bottom < 0 || rect.top > window.innerHeight;
      bar.hidden = !past;
    }
    window.addEventListener('scroll', checkScroll, { passive: true });
    checkScroll();

    // Mirror price changes from variant picker
    if (mainPrice && stickyPrice) {
      const observer = new MutationObserver(function () {
        stickyPrice.textContent = mainPrice.textContent;
      });
      observer.observe(mainPrice, { childList: true, characterData: true, subtree: true });
    }

    // Clicking sticky button submits the real form
    if (stickyBtn) {
      stickyBtn.addEventListener('click', function (e) {
        e.preventDefault();
        mainForm.requestSubmit ? mainForm.requestSubmit() : mainForm.submit();
      });
    }
  }

  // ============ RECENTLY VIEWED ============
  function initRecentlyViewed() {
    const KEY = 'hbm_recently_viewed_handles';
    const MAX = 8;
    const section = document.querySelector('[data-recently-viewed]');
    if (!section) return;

    const grid = section.querySelector('[data-recently-viewed-grid]');
    if (!grid) return;

    // Read current product handle from URL (if on a product page)
    const path = window.location.pathname;
    const handleMatch = path.match(/^\/products\/([^/?#]+)/);
    const currentHandle = handleMatch ? handleMatch[1] : null;

    let handles = [];
    try { handles = JSON.parse(localStorage.getItem(KEY) || '[]'); } catch (e) { handles = []; }

    if (currentHandle) {
      handles = handles.filter(function (h) { return h !== currentHandle; });
      handles.unshift(currentHandle);
      if (handles.length > MAX) handles = handles.slice(0, MAX);
      try { localStorage.setItem(KEY, JSON.stringify(handles)); } catch (e) {}
    }

    const toShow = handles.filter(function (h) { return h !== currentHandle; }).slice(0, 4);
    if (toShow.length === 0) return;

    Promise.all(toShow.map(function (h) {
      return fetch('/products/' + h + '.js')
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    })).then(function (products) {
      products = products.filter(Boolean);
      if (products.length === 0) return;

      grid.innerHTML = products.map(function (p) {
        const img = p.featured_image || (p.images && p.images[0]) || '';
        const onSale = p.compare_at_price && p.compare_at_price > p.price;
        return (
          '<div class="product-card">' +
          '<div class="product-img">' +
          '<a href="' + escapeHtml(p.url) + '" class="product-card-img-link" aria-label="' + escapeHtml(p.title) + '">' +
          (onSale ? '<span class="product-tag">Sale</span>' : '') +
          (img ? '<img src="' + escapeHtml(img) + '" alt="' + escapeHtml(p.title) + '" loading="lazy">' : '') +
          '</a>' +
          '<a href="' + escapeHtml(p.url) + '" class="product-quick product-quick-link">View product →</a>' +
          '</div>' +
          '<a href="' + escapeHtml(p.url) + '" class="product-card-info-link">' +
          (p.vendor ? '<div class="product-brand">' + escapeHtml(p.vendor) + '</div>' : '') +
          '<div class="product-name">' + escapeHtml(p.title) + '</div>' +
          '<div class="product-price"><span class="now">' + formatMoney(p.price) + '</span>' +
          (onSale ? '<span class="was">' + formatMoney(p.compare_at_price) + '</span>' : '') +
          '</div>' +
          '</a>' +
          '</div>'
        );
      }).join('');

      section.style.display = '';
    });
  }

  // ============ PRODUCT RECOMMENDATIONS ============
  function initRecommendations() {
    const section = document.querySelector('[data-product-recommendations]');
    if (!section) return;

    // If server already rendered products, do nothing
    if (section.querySelector('.product-grid .product-card')) return;

    const productId = section.dataset.productId;
    const limit = section.dataset.limit || 4;
    if (!productId) { section.style.display = 'none'; return; }

    // Hide during fetch to avoid empty-header flash
    section.style.visibility = 'hidden';

    // Shopify Section Rendering API: section_id must be the section file name
    fetch('/recommendations/products?section_id=product-recommendations&product_id=' + productId + '&limit=' + limit)
      .then(function (r) { return r.text(); })
      .then(function (html) {
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        const newGrid = tmp.querySelector('.product-grid');
        if (newGrid && newGrid.children.length > 0) {
          const oldGrid = section.querySelector('.product-grid');
          if (oldGrid) oldGrid.innerHTML = newGrid.innerHTML;
          section.style.visibility = '';
        } else {
          section.style.display = 'none';
        }
      })
      .catch(function () { section.style.display = 'none'; });
  }

  // ============ INIT ============
  // Convert product titles stored in ALL CAPS in Shopify (e.g. JUUCE products) into Title
  // Case so they visually match mixed-case products (e.g. Wella, De Lorenzo). Mixed-case
  // titles and short tokens (units like "1L", "ml", "PACK") are preserved as-is for
  // brand abbreviations like RPR, NAK, etc.
  function initProductNameCase() {
    const KEEP_UPPER = new Set(['1L', '2L', '5L', 'L', 'ML', 'OZ', 'KG', 'G', 'UV', 'AU', 'AUD', 'PH', 'CC', 'XL', 'XXL']);
    const KEEP_LOWER = new Set(['a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'in', 'of', 'on', 'or', 'the', 'to', 'with']);

    function toTitle(str) {
      // Tokenise but keep separators (spaces, hyphens, slashes, parentheses)
      return str.split(/(\s+|[-/()])/).map((part, i, arr) => {
        if (!part || /^[\s\-/()]+$/.test(part)) return part;
        const upper = part.toUpperCase();
        // Preserve units and short abbreviations exactly
        if (KEEP_UPPER.has(upper)) return upper;
        // Pure number or unit like "1L", "500ml"
        if (/^\d+[A-Za-z]?$/.test(part)) return part.toUpperCase();
        // Brand acronyms 2-3 letters surrounded by spaces/start/end — keep upper
        if (part.length <= 3 && /^[A-Z]+$/.test(part)) return upper;
        // Small words mid-string stay lowercase (but capitalise if first word)
        const lower = part.toLowerCase();
        if (i > 0 && KEEP_LOWER.has(lower)) return lower;
        return lower.charAt(0).toUpperCase() + lower.slice(1);
      }).join('');
    }

    document.querySelectorAll('.product-name').forEach(el => {
      const original = el.textContent.trim();
      if (!original || original.length < 5) return;
      // Only convert if it's effectively ALL CAPS (allowing digits/symbols/spaces)
      const letters = original.replace(/[^A-Za-z]/g, '');
      if (letters && letters === letters.toUpperCase() && letters.length >= 5) {
        el.textContent = toTitle(original);
      }
    });
  }


  // ============ HERO SLIDESHOW ============
  function initHeroSlideshow() {
    document.querySelectorAll('[data-hero-slideshow]').forEach(root => {
      const slides = root.querySelectorAll('.hero-slide');
      const dots = root.querySelectorAll('.hero-dot');
      const prev = root.querySelector('[data-slide-prev]');
      const next = root.querySelector('[data-slide-next]');
      if (slides.length < 2) return;

      const interval = parseInt(root.dataset.interval, 10) || 6000;
      const autoplay = root.dataset.autoplay !== 'false';
      let index = 0;
      let timer = null;

      function show(i) {
        index = (i + slides.length) % slides.length;
        slides.forEach((s, n) => s.classList.toggle('is-active', n === index));
        dots.forEach((d, n) => {
          d.classList.toggle('is-active', n === index);
          d.setAttribute('aria-selected', n === index ? 'true' : 'false');
        });
      }
      function start() {
        if (!autoplay || timer) return;
        timer = setInterval(() => show(index + 1), interval);
      }
      function stop() { clearInterval(timer); timer = null; }
      function restart() { stop(); start(); }

      dots.forEach(d => d.addEventListener('click', () => {
        show(parseInt(d.dataset.slideTarget, 10));
        restart();
      }));
      if (prev) prev.addEventListener('click', () => { show(index - 1); restart(); });
      if (next) next.addEventListener('click', () => { show(index + 1); restart(); });

      // Pause on hover (desktop), resume on leave
      root.addEventListener('mouseenter', stop);
      root.addEventListener('mouseleave', start);
      // Pause when tab hidden — saves battery and avoids jumping when refocused
      document.addEventListener('visibilitychange', () => {
        document.hidden ? stop() : start();
      });

      // Swipe support on touch devices
      let touchX = null;
      root.addEventListener('touchstart', e => { touchX = e.touches[0].clientX; }, {passive: true});
      root.addEventListener('touchend', e => {
        if (touchX == null) return;
        const dx = e.changedTouches[0].clientX - touchX;
        if (Math.abs(dx) > 50) { show(dx < 0 ? index + 1 : index - 1); restart(); }
        touchX = null;
      }, {passive: true});

      start();
    });
  }

  // ============ TESTIMONIALS ROTATOR ============
  function initTestimonials() {
    document.querySelectorAll('[data-testimonials]').forEach(root => {
      const cards = root.querySelectorAll('.testimonial-card');
      const dots = root.querySelectorAll('.testimonial-dot');
      if (cards.length < 2) return;
      const interval = parseInt(root.dataset.interval, 10) || 7000;
      const autoplay = root.dataset.autoplay !== 'false';
      let index = 0;
      let timer = null;

      function show(i) {
        index = (i + cards.length) % cards.length;
        cards.forEach((c, n) => c.classList.toggle('is-active', n === index));
        dots.forEach((d, n) => {
          d.classList.toggle('is-active', n === index);
          d.setAttribute('aria-selected', n === index ? 'true' : 'false');
        });
      }
      function start() { if (autoplay && !timer) timer = setInterval(() => show(index + 1), interval); }
      function stop() { clearInterval(timer); timer = null; }
      dots.forEach(d => d.addEventListener('click', () => { show(parseInt(d.dataset.testimonialTarget, 10)); stop(); start(); }));
      root.addEventListener('mouseenter', stop);
      root.addEventListener('mouseleave', start);
      start();
    });
  }

  function initAll() {
    initMobileDrawer();
    initPredictiveSearch();
    initCartDrawer();
    initAjaxAddToCart();
    initQuickAdd();
    initProductGallery();
    initVariantPicker();
    initCollectionFilters();
    initCartQty();
    initNewsletter();
    initCookieBanner();
    initImageZoom();
    initStickyAtc();
    initRecentlyViewed();
    initRecommendations();
    initProductNameCase();
    initHeroSlideshow();
    initTestimonials();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
